import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode, useRef } from 'react';
import { api } from '../services/api';
import { CalendarItem, UnifiedEvent } from '../types/auth';
import { useAuth } from './AuthContext';
import { getOfflineRegistrationIds } from '../utils/offlineEventRegistrations';

export interface CalendarContextValue {
  // ids of lives the user has in their calendar (upcoming only; authoritative set)
  reminderIds: Set<number>;
  // list of items for the currently-viewed month (or upcoming when no month selected)
  items: CalendarItem[];
  // set of ISO date strings (YYYY-MM-DD) that have at least one calendar item
  markedDates: Set<string>;
  // count of newly-added events since last viewed (header badge)
  newlyAddedCount: number;
  isLoading: boolean;
  error: string | null;

  addToCalendar: (liveId: number) => Promise<void>;
  removeFromCalendar: (liveId: number) => Promise<void>;
  markViewed: () => Promise<void>;
  /** Refresh items + badge. Pass month (YYYY-MM) to fetch a specific month. */
  refresh: (month?: string) => Promise<void>;
  isInCalendar: (liveId: number) => boolean;
  offlineRegistrationIds: Set<number>;
  offlineRegistrationPendingIds: Set<number>;
  offlineRegistrationErrorIds: Set<number>;
  registerOfflineEvent: (event: Pick<UnifiedEvent, 'id'>) => Promise<void>;
  unregisterOfflineEvent: (event: Pick<UnifiedEvent, 'id'>) => Promise<void>;
  toggleOfflineEventRegistration: (event: Pick<UnifiedEvent, 'id'>) => Promise<void>;
  seedOfflineEventRegistration: (eventId: number, registered: boolean) => void;
}

const CalendarContext = createContext<CalendarContextValue | null>(null);

export const CalendarProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [reminderIds, setReminderIds] = useState<Set<number>>(new Set());
  const [items, setItems] = useState<CalendarItem[]>([]);
  const [newlyAddedCount, setNewlyAddedCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadedUserRef = useRef<string | null>(null);
  const [offlineRegistrationIds, setOfflineRegistrationIds] = useState<Set<number>>(new Set());
  const [offlineRegistrationPendingIds, setOfflineRegistrationPendingIds] = useState<Set<number>>(new Set());
  const [offlineRegistrationErrorIds, setOfflineRegistrationErrorIds] = useState<Set<number>>(new Set());
  const offlineRegistrationIdsRef = useRef<Set<number>>(new Set());
  const offlineRegistrationPendingIdsRef = useRef<Set<number>>(new Set());

  // Initial load on login / clear on logout
  useEffect(() => {
    if (!isAuthenticated || !user?.id) {
      setReminderIds(new Set());
      setItems([]);
      setNewlyAddedCount(0);
      setError(null);
      offlineRegistrationIdsRef.current = new Set();
      offlineRegistrationPendingIdsRef.current = new Set();
      setOfflineRegistrationIds(new Set());
      setOfflineRegistrationPendingIds(new Set());
      setOfflineRegistrationErrorIds(new Set());
      loadedUserRef.current = null;
      return;
    }
    if (loadedUserRef.current === user.id) return;
    loadedUserRef.current = user.id;

    (async () => {
      setIsLoading(true);
      try {
        const [list, badge, registrations] = await Promise.all([
          api.getUserCalendar().catch(() => [] as CalendarItem[]),
          api.getCalendarBadge().catch(() => ({ count: 0 })),
          api.getUserRegistrations({ limit: 500 }).catch(() => ({ items: [] as unknown[] })),
        ]);
        setItems(list);
        setReminderIds(new Set(list.map(i => i.id)));
        setNewlyAddedCount(badge.count);
        const registeredIds = getOfflineRegistrationIds(registrations.items || []);
        offlineRegistrationIdsRef.current = registeredIds;
        setOfflineRegistrationIds(registeredIds);
        setError(null);
      } catch (err: any) {
        console.warn('[Calendar] Initial load failed:', err?.message);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [isAuthenticated, user?.id]);

  const addToCalendar = useCallback(async (liveId: number) => {
    if (!isAuthenticated) return;
    // Optimistic update for reminderIds — the «Я иду» button flips immediately.
    const wasAlreadyIn = reminderIds.has(liveId);
    if (!wasAlreadyIn) {
      setReminderIds(prev => {
        const next = new Set(prev);
        next.add(liveId);
        return next;
      });
      setNewlyAddedCount(n => n + 1);
    }
    try {
      await api.addLiveToCalendar(liveId);
      // After BE confirms, re-pull items so `markedDates` and `items` reflect
      // the new subscription. Without this, the calendar dot for the newly
      // subscribed day never appears (group B bug from 2026-05-04 smoke).
      const list = await api.getUserCalendar().catch(() => null as CalendarItem[] | null);
      if (list) {
        setItems(list);
        setReminderIds(new Set(list.map(i => i.id)));
      }
    } catch (err: any) {
      console.warn('[Calendar] addToCalendar API failed, keeping optimistic state:', err?.message);
    }
  }, [isAuthenticated, reminderIds]);

  const removeFromCalendar = useCallback(async (liveId: number) => {
    if (!isAuthenticated) return;
    const wasIn = reminderIds.has(liveId);
    // Optimistic
    if (wasIn) {
      setReminderIds(prev => {
        const next = new Set(prev);
        next.delete(liveId);
        return next;
      });
      setItems(list => list.filter(i => i.id !== liveId));
    }
    try {
      await api.removeLiveFromCalendar(liveId);
    } catch (err: any) {
      console.warn('[Calendar] removeFromCalendar API failed, keeping optimistic state:', err?.message);
      // Keep optimistic state so the UI doesn't flicker.
    }
  }, [isAuthenticated, reminderIds, items]);

  const markViewed = useCallback(async () => {
    if (!isAuthenticated) return;
    setNewlyAddedCount(0);
    try {
      await api.markCalendarViewed();
    } catch {
      // non-critical
    }
  }, [isAuthenticated]);

  const refresh = useCallback(async (month?: string) => {
    if (!isAuthenticated) return;
    setIsLoading(true);
    try {
      const [list, badge] = await Promise.all([
        api.getUserCalendar({ month }),
        api.getCalendarBadge(),
      ]);
      setItems(list);
      if (!month) {
        // month-free fetch → update the global reminderIds set
        setReminderIds(new Set(list.map(i => i.id)));
      }
      setNewlyAddedCount(badge.count);
      setError(null);
    } catch (err: any) {
      setError(err?.message || 'Failed to refresh calendar');
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  const isInCalendar = useCallback((liveId: number) => reminderIds.has(liveId), [reminderIds]);

  const seedOfflineEventRegistration = useCallback((eventId: number, registered: boolean) => {
    const alreadyRegistered = offlineRegistrationIdsRef.current.has(eventId);
    if (alreadyRegistered === registered) return;
    const next = new Set(offlineRegistrationIdsRef.current);
    if (registered) next.add(eventId);
    else next.delete(eventId);
    offlineRegistrationIdsRef.current = next;
    setOfflineRegistrationIds(next);
  }, []);

  const runOfflineRegistration = useCallback(async (event: Pick<UnifiedEvent, 'id'>, register: boolean) => {
    if (!isAuthenticated) return;
    if (offlineRegistrationPendingIdsRef.current.has(event.id)) return;
    if (offlineRegistrationIdsRef.current.has(event.id) === register) return;

    offlineRegistrationPendingIdsRef.current.add(event.id);
    setOfflineRegistrationPendingIds(new Set(offlineRegistrationPendingIdsRef.current));
    setOfflineRegistrationErrorIds(prev => {
      if (!prev.has(event.id)) return prev;
      const next = new Set(prev);
      next.delete(event.id);
      return next;
    });

    try {
      if (register) await api.registerForEvent('master', event.id);
      else await api.unregisterFromEvent('master', event.id);
      seedOfflineEventRegistration(event.id, register);
    } catch (err) {
      setOfflineRegistrationErrorIds(prev => new Set(prev).add(event.id));
      throw err;
    } finally {
      offlineRegistrationPendingIdsRef.current.delete(event.id);
      setOfflineRegistrationPendingIds(new Set(offlineRegistrationPendingIdsRef.current));
    }
  }, [isAuthenticated, seedOfflineEventRegistration]);

  const registerOfflineEvent = useCallback(
    (event: Pick<UnifiedEvent, 'id'>) => runOfflineRegistration(event, true),
    [runOfflineRegistration],
  );
  const unregisterOfflineEvent = useCallback(
    (event: Pick<UnifiedEvent, 'id'>) => runOfflineRegistration(event, false),
    [runOfflineRegistration],
  );
  const toggleOfflineEventRegistration = useCallback(
    (event: Pick<UnifiedEvent, 'id'>) => runOfflineRegistration(event, !offlineRegistrationIdsRef.current.has(event.id)),
    [runOfflineRegistration],
  );

  // Derive the set of ISO date strings (YYYY-MM-DD) that have at least one
  // calendar item. CORE-225 — a multi-day item (startDateTimeUtc +
  // durationMinutes spanning days) marks every day it spans, not just the start.
  const markedDates = useMemo(() => {
    const set = new Set<string>();
    const iso = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    items.forEach(i => {
      const start = new Date(i.startDateTimeUtc).getTime();
      if (!Number.isFinite(start)) return;
      const endTime = start + Math.max(0, (i.durationMinutes ?? 0) * 60_000);
      const cur = new Date(start);
      cur.setHours(0, 0, 0, 0);
      const endDay = new Date(endTime);
      endDay.setHours(0, 0, 0, 0);
      for (let k = 0; k <= 366 && cur.getTime() <= endDay.getTime(); k++) {
        set.add(iso(cur));
        cur.setDate(cur.getDate() + 1);
      }
    });
    return set;
  }, [items]);

  return (
    <CalendarContext.Provider
      value={{
        reminderIds,
        items,
        markedDates,
        newlyAddedCount,
        isLoading,
        error,
        addToCalendar,
        removeFromCalendar,
        markViewed,
        refresh,
        isInCalendar,
        offlineRegistrationIds,
        offlineRegistrationPendingIds,
        offlineRegistrationErrorIds,
        registerOfflineEvent,
        unregisterOfflineEvent,
        toggleOfflineEventRegistration,
        seedOfflineEventRegistration,
      }}
    >
      {children}
    </CalendarContext.Provider>
  );
};

export const useCalendar = (): CalendarContextValue => {
  const context = useContext(CalendarContext);
  if (!context) {
    throw new Error('useCalendar must be used within a CalendarProvider');
  }
  return context;
};
